import React, { useState, useRef, useEffect } from 'react';
import CyclingTitle from './primitives/CyclingTitle';
import { useGoogleCalendarSync } from '../hooks/useGoogleCalendarSync';
import { getGoogleAuthUrl } from '../utils/googleCalendarApi';
import { formatRelativeDate, formatRelativeTime } from '../utils/formatDate';

// ── Season helper ─────────────────────────────────────────────────────────────
function getCurrentSeason() {
  const m = new Date().getMonth() + 1, d = new Date().getDate();
  if ((m === 3 && d >= 20) || m === 4 || m === 5 || (m === 6 && d < 21)) return 'spring';
  if ((m === 6 && d >= 21) || m === 7 || m === 8 || (m === 9 && d < 22)) return 'summer';
  if ((m === 9 && d >= 22) || m === 10 || m === 11 || (m === 12 && d < 21)) return 'autumn';
  return 'winter';
}

const DEFAULT_WORK_KW = ['shift','er ','northshore','endeavor','nocturnist','overnight','7p','7a','19:00','on call'];
const DEFAULT_CATS = ['Housing','Food','Transport','Health','Entertainment','Shopping','Utilities','Subscriptions','Other'];
const BUILTIN_TYPES = ['Easy Run','Long Run','Tempo Run','Intervals','Strength','Gym','Soccer','Cross Train','Rest'];

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
.sp-page{max-width:640px;margin:0 auto;padding:var(--sp4) var(--sp4) var(--sp12);font-family:var(--sans);}
@media(max-width:768px){.sp-page{padding-bottom:calc(var(--bot-nav-h,56px)+var(--sp10)+var(--safe-bottom,0px));}}
.sp-hdr{margin-bottom:var(--sp5);}
.sp-section{margin-bottom:var(--sp5);}
.sp-section-lbl{font-family:var(--sans);font-size:11px;font-weight:400;color:var(--t3);letter-spacing:0.5px;text-transform:lowercase;margin-bottom:8px;}
.sp-card{background:var(--s1);border:0.5px solid var(--s2);border-radius:var(--r3);overflow:hidden;}
.sp-row{display:flex;align-items:center;justify-content:space-between;gap:var(--sp3);padding:12px 16px;border-bottom:0.5px solid var(--s2);}
.sp-row:last-child{border-bottom:none;}
.sp-row-lbl{font-family:var(--sans);font-size:13px;color:var(--ink);margin-bottom:2px;}
.sp-row-sub{font-family:var(--sans);font-size:11px;color:var(--t3);}
.sp-row-val{font-family:var(--sans);font-size:12px;color:var(--t3);flex-shrink:0;}
.sp-input{height:36px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r2);padding:0 10px;font-family:var(--sans);font-size:13px;color:var(--ink);outline:none;min-width:180px;transition:border-color .1s;}
.sp-input:focus{border-color:var(--accent);}
.sp-input-full{width:100%;box-sizing:border-box;}
.sp-select{height:36px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r2);padding:0 10px;font-family:var(--sans);font-size:13px;color:var(--ink);outline:none;cursor:pointer;appearance:none;}
.sp-btn{height:28px;padding:0 14px;background:var(--accent);border:none;border-radius:var(--r2);font-family:var(--sans);font-size:var(--fs-xs);font-weight:500;color:var(--on-accent);cursor:pointer;white-space:nowrap;transition:opacity .12s;flex-shrink:0;}
.sp-btn:hover{opacity:.88;}
.sp-btn-outline{background:transparent;border:0.5px solid var(--b2);color:var(--t2);}
.sp-btn-outline:hover{background:var(--s2);opacity:1;}
.sp-btn-danger{background:transparent;border:0.5px solid var(--s3);color:var(--t4);}
.sp-link{font-family:var(--sans);font-size:12px;color:var(--accent);background:transparent;border:none;cursor:pointer;padding:0;}
.sp-link-dim{color:var(--t4);}
.sp-note{font-family:var(--sans);font-size:11px;color:var(--t4);padding:8px 16px;border-top:0.5px solid var(--s2);}
.sp-field-block{padding:12px 16px;border-bottom:0.5px solid var(--s2);display:flex;flex-direction:column;gap:8px;}
.sp-field-block:last-child{border-bottom:none;}
.sp-field-lbl{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:0.04em;text-transform:lowercase;margin-bottom:4px;}
.sp-pill-row{display:flex;gap:4px;flex-wrap:wrap;}
.sp-pill{height:26px;padding:0 10px;border-radius:var(--r2);border:0.5px solid var(--b2);background:var(--s2);font-family:var(--sans);font-size:var(--fs-xs);color:var(--t3);cursor:pointer;transition:all .1s;}
.sp-pill-active{background:var(--accent);border-color:var(--accent);color:var(--on-accent);font-weight:500;}
/* list rows */
.sp-list-row{display:flex;align-items:center;justify-content:space-between;padding:9px 16px;border-bottom:0.5px solid var(--s2);}
.sp-list-row:last-child{border-bottom:none;}
.sp-list-name{font-family:var(--sans);font-size:13px;color:var(--ink);}
.sp-list-name-dim{color:var(--t4);}
.sp-list-rm{background:transparent;border:none;color:var(--t4);cursor:pointer;font-size:14px;padding:0 4px;}
.sp-list-add{padding:9px 16px;display:flex;gap:6px;}
.sp-list-add-input{flex:1;height:30px;background:var(--s2);border:0.5px solid var(--b2);border-radius:var(--r);padding:0 8px;font-family:var(--sans);font-size:12px;color:var(--ink);outline:none;}
/* mode toggle */
.sp-mode-toggle{display:flex;background:var(--s2);border-radius:9px;padding:3px;position:relative;flex-shrink:0;}
.sp-mode-btn{padding:6px 14px;font-size:12px;color:var(--t3);background:transparent;border:none;border-radius:7px;cursor:pointer;position:relative;z-index:1;font-family:var(--sans);letter-spacing:0.2px;transition:color .18s;white-space:nowrap;}
.sp-mode-btn-active{color:var(--ink);}
.sp-mode-thumb{position:absolute;top:3px;left:3px;height:calc(100% - 6px);background:var(--s1);border-radius:7px;box-shadow:0 1px 2px rgba(0,0,0,0.08);transition:transform .18s ease,width .18s ease;z-index:0;}
/* kw chips */
.sp-kw-chips{display:flex;flex-wrap:wrap;gap:6px;padding:10px 16px;}
.sp-kw-chip{display:flex;align-items:center;gap:4px;background:var(--s2);border:0.5px solid var(--b1);border-radius:var(--r);padding:2px 8px;}
.sp-kw-text{font-family:var(--sans);font-size:11px;color:var(--t2);}
.sp-kw-rm{background:transparent;border:none;color:var(--t4);cursor:pointer;padding:0;font-size:12px;line-height:1;}
/* toggle */
.sp-toggle{width:38px;height:22px;border-radius:11px;border:none;cursor:pointer;position:relative;transition:background .15s;flex-shrink:0;}
.sp-toggle-on{background:var(--accent);}
.sp-toggle-off{background:var(--s3);}
.sp-toggle-thumb{position:absolute;top:3px;width:16px;height:16px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.2);transition:transform .15s;}
/* avatar */
.sp-avatar{width:40px;height:40px;border-radius:50%;background:var(--s2);display:flex;align-items:center;justify-content:center;font-family:var(--sans);font-size:17px;font-weight:500;color:var(--ink);flex-shrink:0;}
/* confirm danger */
.sp-confirm-row{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;border-bottom:0.5px solid var(--s2);}
.sp-confirm-row:last-child{border-bottom:none;}
/* footer */
.sp-footer{text-align:center;padding:var(--sp8) 0 var(--sp4);font-family:var(--sans);font-size:10px;color:var(--t4);}
`;

// ── Mode Toggle component (sliding thumb) ─────────────────────────────────────
function ModeToggle({ value, onChange }) {
  const lightRef = useRef(null);
  const darkRef  = useRef(null);
  const thumbRef = useRef(null);
  useEffect(()=>{
    const active = value === 'light' ? lightRef.current : darkRef.current;
    if (active && thumbRef.current) {
      thumbRef.current.style.width     = active.offsetWidth + 'px';
      thumbRef.current.style.transform = value === 'light' ? 'translateX(0)' : `translateX(${lightRef.current?.offsetWidth || 0}px)`;
    }
  },[value]);
  return (
    <div className="sp-mode-toggle">
      <div className="sp-mode-thumb" ref={thumbRef}/>
      {['light','dark'].map(m=>(
        <button key={m} ref={m==='light'?lightRef:darkRef}
          className={`sp-mode-btn ${value===m?'sp-mode-btn-active':''}`}
          onClick={()=>onChange(m)}>{m}</button>
      ))}
    </div>
  );
}

// ── List editor (categories + custom types) ───────────────────────────────────
function ListEditor({ items, onAdd, onRemove, placeholder, readonlyItems=[] }) {
  const [input, setInput] = useState('');
  function add() { const v=input.trim(); if(v){onAdd(v);setInput('');} }
  return (
    <div className="sp-card">
      {readonlyItems.map((item,i)=>(
        <div key={i} className="sp-list-row">
          <span className="sp-list-name sp-list-name-dim">{(item||"").toLowerCase()}</span>
        </div>
      ))}
      {items.map((item,i)=>(
        <div key={i} className="sp-list-row">
          <span className="sp-list-name">{(item||"").toLowerCase()}</span>
          <button className="sp-list-rm" onClick={()=>onRemove(item)}>×</button>
        </div>
      ))}
      <div className="sp-list-add">
        <input className="sp-list-add-input" placeholder={placeholder}
          value={input} onChange={e=>setInput(e.target.value)}
          onKeyDown={e=>{ if(e.key==='Enter') add(); }}/>
        <button className="sp-btn" onClick={add}>+ add</button>
      </div>
    </div>
  );
}

// ── MAIN ──────────────────────────────────────────────────────────────────────
export default function SettingsPage({ st, prefs, anthropicKey, setSt, memories, subscribeToPush, discogsToken, setTab, notify }) {
  const VIEWS = ['profile','display','sync','advanced'];
  const [view, setView] = useState('profile');
  const [editingListId, setEditingListId] = useState(null);
  const [editingListName, setEditingListName] = useState('');

  // Profile state
  const [name,     setName]     = useState(st.profile?.name || '');
  const [dob,      setDob]      = useState(st.profile?.dob || '');
  const [timezone, setTimezone] = useState(st.profile?.timezone || 'America/Chicago');
  const [raceName, setRaceName] = useState(st.raceName || '');
  const [raceDate, setRaceDate] = useState(st.raceDate || '');

  // Advanced state
  const [clearConfirm, setClearConfirm] = useState(false);
  const [newKw, setNewKw]     = useState('');
  const [newCat, setNewCat]   = useState('');
  const [newType, setNewType] = useState('');
  const [icsMsg, setIcsMsg]   = useState('');

  // GCal sync hook
  const { syncFromGoogle, syncing: gcalSyncing, lastSynced, syncError } = useGoogleCalendarSync({ setSt, events: st.events || [] });

  const season        = getCurrentSeason();
  const theme         = st.theme || 'dark';
  const workKeywords  = st.workKeywords || [];
  const categories    = st.financeCategories || st.categories || [];
  const customTypes   = st.customWorkoutTypes || [];
  const gcalConnected = true; // determined by whether tokens exist — shown via sync status

  // ── Helpers ────────────────────────────────────────────────────────────────
  function saveProfile() {
    setSt(p=>({...p, profile:{...(p.profile||{}), name, timezone, dob}}));
    if(notify) notify('Profile saved');
  }

  function saveRace() {
    if(!raceName||!raceDate) return;
    setSt(p=>({...p, raceName: raceName.trim(), raceDate: raceDate}));
    if(notify) notify('Race saved');
  }

  function importICS(txt) {
    if(!txt) return;
    try {
      const evs=[]; const lines=txt.replace(/\r\n/g,'\n').replace(/\r/g,'\n').split('\n');
      let cur=null;
      for(const line of lines){
        if(line==='BEGIN:VEVENT'){cur={title:'',date:'',time:'',endTime:'',type:'event',source:'ics'};}
        else if(line==='END:VEVENT'&&cur){if(cur.date)evs.push(cur);cur=null;}
        else if(cur){
          if(line.startsWith('SUMMARY:'))cur.title=line.slice(8).trim()||'Untitled';
          else if(line.startsWith('DTSTART')){
            const raw=line.split(':')[1]?.trim()||'';
            if(raw.length>=8){cur.date=`${raw.slice(0,4)}-${raw.slice(4,6)}-${raw.slice(6,8)}`;}
            if(raw.length>8){let h=parseInt(raw.slice(9,11));const mn=raw.slice(11,13);const ap=h>=12?'PM':'AM';if(h>12)h-=12;if(h===0)h=12;cur.time=`${h}:${mn} ${ap}`;}
          }
          else if(line.startsWith('DTEND')){
            const raw=line.split(':')[1]?.trim()||'';
            if(raw.length>8){let h=parseInt(raw.slice(9,11));const mn=raw.slice(11,13);const ap=h>=12?'PM':'AM';if(h>12)h-=12;if(h===0)h=12;cur.endTime=`${h}:${mn} ${ap}`;}
          }
          if(!cur.type)cur.type='event';
        }
      }
      setSt(p=>{
        const ex=new Set((p.events||[]).map(e=>`${e.date}|${(e.title||'').toLowerCase()}`));
        const toAdd=evs.filter(e=>!ex.has(`${e.date}|${(e.title||'').toLowerCase()}`))
          .map(e=>({id:`ics${Date.now()}${Math.random()}`, ...e}));
        return{...p,events:[...p.events,...toAdd]};
      });
      setIcsMsg(`✓ ${evs.length} events imported`);
    } catch(e) { setIcsMsg('Error: '+e.message); }
  }

  function exportData() {
    const blob=new Blob([JSON.stringify(st,null,2)],{type:'application/json'});
    const a=document.createElement('a'); a.href=URL.createObjectURL(blob); a.download='mylo-data.json'; a.click();
  }

  function clearData() {
    if(!clearConfirm){setClearConfirm(true);setTimeout(()=>setClearConfirm(false),5000);return;}
    setSt({});setClearConfirm(false);
    if(notify)notify('All data cleared');
  }

  
  const racePast = st.raceDate && st.raceDate < new Date().toISOString().slice(0,10);

  return (
    <>
      <style>{CSS}</style>
      <div className="sp-page">
        <div className="sp-hdr">
          <CyclingTitle pageLabel="settings" filters={VIEWS} value={view} onChange={setView} alwaysShowSuffix={true}/>
        </div>

        {/* ══ PROFILE ══ */}
        {view==='profile'&&(
          <>
            {/* Account */}
            <div className="sp-section">
              <div className="sp-section-lbl">account</div>
              <div className="sp-card">
                <div className="sp-field-block">
                  <div className="sp-row" style={{padding:0,border:'none'}}>
                    <div className="sp-avatar">{(name||'K').slice(0,1).toUpperCase()}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div className="sp-field-lbl">name</div>
                      <input className="sp-input sp-input-full" value={name} onChange={e=>setName(e.target.value)} placeholder="your name"/>
                    </div>
                  </div>
                </div>
                <div className="sp-field-block">
                  <div className="sp-field-lbl">timezone</div>
                  <input className="sp-input sp-input-full" value={timezone} onChange={e=>setTimezone(e.target.value)} placeholder="America/Chicago"/>
                </div>
                <div className="sp-field-block">
                  <div className="sp-field-lbl">date of birth</div>
                  <input type="date" className="sp-input" value={dob} onChange={e=>setDob(e.target.value)}/>
                  {dob && <div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)'}}>age: {Math.floor((new Date()-new Date(dob))/(365.25*86400000))} · used for projection engine</div>}
                </div>
                <div style={{padding:'10px 16px',display:'flex',justifyContent:'flex-end'}}>
                  <div style={{display:'flex',justifyContent:'flex-end',marginTop:'var(--sp2)'}}><button className="sp-btn" onClick={saveProfile} style={{height:32,padding:'0 16px',fontSize:'var(--fs-xs)'}}>save</button></div>
                </div>
              </div>
            </div>

            {/* Race */}
            <div className="sp-section">
              <div className="sp-section-lbl">race</div>
              <div className="sp-card">
                <div className="sp-field-block">
                  <div className="sp-field-lbl">race name</div>
                  <input className="sp-input sp-input-full" value={raceName} onChange={e=>setRaceName(e.target.value)} placeholder="e.g. brooklyn marathon"/>
                </div>
                <div className="sp-field-block">
                  <div className="sp-field-lbl">race date</div>
                  <input type="date" className="sp-input" value={raceDate} onChange={e=>setRaceDate(e.target.value)}/>
                </div>
                {racePast&&<div className="sp-note">that race has passed — add your next one</div>}
                <div style={{padding:'10px 16px',display:'flex',justifyContent:'flex-end'}}>
                  <div style={{display:'flex',justifyContent:'flex-end',marginTop:'var(--sp2)'}}><button className="sp-btn" onClick={saveRace} style={{height:32,padding:'0 16px',fontSize:'var(--fs-xs)'}}>save</button></div>
                </div>
              </div>
            </div>

            {/* Preferences */}
            <div className="sp-section">
              <div className="sp-section-lbl">preferences</div>
              <div className="sp-card">
                <div className="sp-field-block">
                  <div className="sp-field-lbl">default calendar view</div>
                  <div className="sp-pill-row">
                    {['today','week','month'].map(v=>(
                      <button key={v} className={`sp-pill ${(st.prefs?.defaultCalView||'today')===v?'sp-pill-active':''}`}
                        onClick={()=>setSt(p=>({...p,prefs:{...(p.prefs||{}),defaultCalView:v}}))}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="sp-field-block">
                  <div className="sp-field-lbl">default train view</div>
                  <div className="sp-pill-row">
                    {['log','coach','stats','triumphs'].map(v=>(
                      <button key={v} className={`sp-pill ${(st.prefs?.defaultTrainView||'log')===v?'sp-pill-active':''}`}
                        onClick={()=>setSt(p=>({...p,prefs:{...(p.prefs||{}),defaultTrainView:v}}))}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ══ DISPLAY ══ */}
        {view==='display'&&(
          <>
            {/* Appearance */}
            <div className="sp-section">
              <div className="sp-section-lbl">appearance</div>
              <div className="sp-card">
                <div className="sp-row">
                  <div><div className="sp-row-lbl">appearance</div><div className="sp-row-sub">light or dark mode</div></div>
                  <ModeToggle value={theme} onChange={v=>setSt(p=>({...p,theme:v}))}/>
                </div>
                <div className="sp-row">
                  <div><div className="sp-row-lbl">seasonal accent</div><div className="sp-row-sub">current season: <span style={{color:'var(--accent)'}}>{season}</span></div></div>
                  <span className="sp-row-val">auto</span>
                </div>
                <div className="sp-note">accent color rotates with the season — spring, summer, autumn, winter</div>
              </div>
            </div>

            {/* Finance categories */}
            <div className="sp-section">
              <div className="sp-section-lbl">finance categories</div>
              <ListEditor
                items={categories}
                readonlyItems={DEFAULT_CATS.filter(c=>!categories.includes(c))}
                onAdd={v=>setSt(p=>({...p,financeCategories:[...(p.financeCategories&&p.financeCategories.length>0?p.financeCategories:['Housing','Food','Transport','Health','Entertainment','Subscriptions','Other']),v]}))}
                onRemove={v=>setSt(p=>({...p,financeCategories:(p.financeCategories||[]).filter(c=>c!==v)}))}
                placeholder="add category…"/>
            </div>

            {/* Task lists */}
            <div className="sp-section">
              <div className="sp-section-lbl">task lists</div>
              <div className="sp-card">
                {(st.quickLists||[]).length === 0 && (
                  <div style={{padding:'12px 16px',fontFamily:'var(--sans)',fontSize:13,color:'var(--t4)'}}>no lists yet — create them in Tasks</div>
                )}
                {(st.quickLists||[]).map((list,i) => (
                  <div key={list.id} className="sp-list-row">
                    {editingListId === list.id ? (
                      <input
                        className="sp-list-add-input"
                        style={{flex:1,marginRight:8}}
                        value={editingListName}
                        onChange={e=>setEditingListName(e.target.value)}
                        onKeyDown={e=>{
                          if(e.key==='Enter'&&editingListName.trim()){
                            setSt(p=>({...p,quickLists:(p.quickLists||[]).map(l=>l.id===list.id?{...l,title:editingListName.trim()}:l)}));
                            setEditingListId(null);
                          }
                          if(e.key==='Escape') setEditingListId(null);
                        }}
                        autoFocus
                      />
                    ) : (
                      <span className="sp-list-name" style={{flex:1}}>{list.title?.toLowerCase()}</span>
                    )}
                    {editingListId === list.id ? (
                      <button className="sp-btn" style={{marginRight:4}} onClick={()=>{
                        if(editingListName.trim()) setSt(p=>({...p,quickLists:(p.quickLists||[]).map(l=>l.id===list.id?{...l,title:editingListName.trim()}:l)}));
                        setEditingListId(null);
                      }}>save</button>
                    ) : (
                      <button className="sp-list-rm" style={{marginRight:4,fontSize:12,opacity:0.6}} onClick={()=>{setEditingListId(list.id);setEditingListName(list.title||'');}}>edit</button>
                    )}
                    <button className="sp-list-rm" onClick={()=>{
                      if(window.confirm(`Delete "${list.title}"? This won't delete tasks in the list.`)){
                        setSt(p=>({...p,quickLists:(p.quickLists||[]).filter(l=>l.id!==list.id)}));
                        notify('List deleted');
                      }
                    }}>×</button>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom workout types */}
            <div className="sp-section">
              <div className="sp-section-lbl">workout types</div>
              <ListEditor
                items={customTypes}
                readonlyItems={BUILTIN_TYPES}
                onAdd={v=>setSt(p=>({...p,customWorkoutTypes:[...(p.customWorkoutTypes||[]),v]}))}
                onRemove={v=>setSt(p=>({...p,customWorkoutTypes:(p.customWorkoutTypes||[]).filter(t=>t!==v)}))}
                placeholder="add workout type…"/>
            </div>
          </>
        )}

        {/* ══ SYNC ══ */}
        {view==='sync'&&(
          <>
            {/* Google Calendar */}
            <div className="sp-section">
              <div className="sp-section-lbl">google calendar</div>
              <div className="sp-card">
                <div className="sp-row">
                  <div><div className="sp-row-lbl">status</div><div className="sp-row-sub">{syncError?'connection error':'connected'}</div></div>
                  <span style={{color:syncError?'var(--t4)':'var(--accent)',fontSize:12}}>{syncError?'—':'✓'}</span>
                </div>
                <div className="sp-row">
                  <div><div className="sp-row-lbl">last synced</div><div className="sp-row-sub">{lastSynced?formatRelativeTime(lastSynced):'never'}</div></div>
                  <button className="sp-btn" disabled={gcalSyncing} onClick={()=>syncFromGoogle(false).catch(()=>{})}>
                    {gcalSyncing?'syncing…':'sync now'}
                  </button>
                </div>
                <div className="sp-row">
                  <div><div className="sp-row-lbl">connect account</div><div className="sp-row-sub">authorize google calendar access</div></div>
                  <a href={getGoogleAuthUrl()} className="sp-link">connect →</a>
                </div>
                <div className="sp-row">
                  <div><div className="sp-row-lbl">remove synced events</div><div className="sp-row-sub">clears google events from mylo only</div></div>
                  <button className="sp-btn sp-btn-danger" onClick={()=>{setSt(p=>({...p,events:(p.events||[]).filter(e=>e.source!=='google')}));if(notify)notify('Google events removed');}}>clear</button>
                </div>
              </div>
            </div>

            {/* Gmail */}
            <div className="sp-section">
              <div className="sp-section-lbl">gmail intelligence</div>
              <div className="sp-card">
                <div className="sp-row">
                  <div><div className="sp-row-lbl">status</div><div className="sp-row-sub">{st.gmailConnected ? 'connected · ' + (st.gmailEmail||'') : 'not connected'}</div></div>
                  <span style={{color:st.gmailConnected?'var(--accent)':'var(--t4)',fontSize:12}}>{st.gmailConnected?'✓':'—'}</span>
                </div>
                <div className="sp-row">
                  <div><div className="sp-row-lbl">connect gmail</div><div className="sp-row-sub">auto-detect vinyl orders, bills, deliveries</div></div>
                  <button className="sp-btn" onClick={async function() {
                    try {
                      const keys = Object.keys(localStorage).filter(k => k.includes('auth-token'));
                      const token = keys.length ? JSON.parse(localStorage.getItem(keys[0])).access_token : null;
                      if (!token) { if(notify) notify('Please sign in first'); return; }
                      const res = await fetch('/api/auth/gmail-connect', { headers: { 'Authorization': 'Bearer ' + token } });
                      const data = await res.json();
                      if (data.url) window.open(data.url, '_blank');
                    } catch(e) { if(notify) notify('Error connecting Gmail'); }
                  }}>connect →</button>
                </div>
                {st.gmailConnected && (
                  <div className="sp-row">
                    <div><div className="sp-row-lbl">scan inbox</div><div className="sp-row-sub">check for new orders and deliveries</div></div>
                    <button className="sp-btn" onClick={async function() {
                      try {
                        const keys = Object.keys(localStorage).filter(k => k.includes('auth-token'));
                        const token = keys.length ? JSON.parse(localStorage.getItem(keys[0])).access_token : null;
                        if (!token) return;
                        if(notify) notify('Scanning Gmail…');
                        const res = await fetch('/api/gmail-scan', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
                          body: JSON.stringify({ dryRun: false })
                        });
                        const data = await res.json();
                        if(notify) notify(data.newActions > 0 ? data.newActions + ' new items detected' : 'inbox up to date');
                      } catch(e) { if(notify) notify('Scan failed'); }
                    }}>scan now</button>
                  </div>
                )}
              </div>
            </div>

            {/* Work shift keywords */}
            <div className="sp-section">
              <div className="sp-section-lbl">work shift keywords</div>
              <div className="sp-card">
                <div className="sp-kw-chips">
                  {[...DEFAULT_WORK_KW, ...workKeywords].map((kw,i)=>(
                    <div key={i} className="sp-kw-chip">
                      <span className="sp-kw-text">{kw}</span>
                      {i>=DEFAULT_WORK_KW.length&&<button className="sp-kw-rm" onClick={()=>setSt(p=>({...p,workKeywords:(p.workKeywords||[]).filter(k=>k!==kw)}))}>×</button>}
                    </div>
                  ))}
                </div>
                <div className="sp-list-add">
                  <input className="sp-list-add-input" placeholder="add keyword…" value={newKw} onChange={e=>setNewKw(e.target.value)}
                    onKeyDown={e=>{if(e.key==='Enter'&&newKw.trim()){setSt(p=>({...p,workKeywords:[...(p.workKeywords||[]),newKw.trim()]}));setNewKw('');}}}/>
                  <button className="sp-btn" onClick={()=>{if(newKw.trim()){setSt(p=>({...p,workKeywords:[...(p.workKeywords||[]),newKw.trim()]}));setNewKw('');}}}>+ add</button>
                </div>
              </div>
            </div>

            {/* Apple Calendar */}
            <div className="sp-section">
              <div className="sp-section-lbl">apple calendar</div>
              <div className="sp-card">
                <div className="sp-row">
                  <div><div className="sp-row-lbl">import from apple calendar</div><div className="sp-row-sub">calendar app → right-click → export… → upload .ics</div></div>
                  <button className="sp-btn" onClick={()=>document.getElementById('ics-upload-settings').click()}>upload .ics</button>
                </div>
                <input type="file" accept=".ics" id="ics-upload-settings" style={{display:'none'}}
                  onChange={e=>{
                    const file=e.target.files?.[0]; if(!file)return;
                    setIcsMsg(`Reading ${file.name}…`);
                    const reader=new FileReader();
                    reader.onload=ev=>{if(typeof ev.target?.result==='string')importICS(ev.target.result);else setIcsMsg('Error: could not read file');};
                    reader.onerror=()=>setIcsMsg('Error: file read failed');
                    reader.readAsText(file); e.target.value='';
                  }}/>
                {icsMsg&&<div className="sp-note" style={{color:icsMsg.startsWith('✓')?'var(--accent)':icsMsg.startsWith('Reading')?'var(--t3)':'var(--t4)'}}>{icsMsg}</div>}
              </div>
            </div>

            {/* Discogs */}
            <div className="sp-section">
              <div className="sp-section-lbl">discogs</div>
              <div className="sp-card">
                <div className="sp-field-block">
                  <div className="sp-field-lbl">username</div>
                  <input className="sp-input sp-input-full" placeholder="discogs username"
                    value={st.profile?.discogsUser||''} onChange={e=>setSt(p=>({...p,profile:{...(p.profile||{}),discogsUser:e.target.value}}))}/>
                </div>
                <div className="sp-field-block">
                  <div className="sp-field-lbl">api token</div>
                  <input type="password" className="sp-input sp-input-full" placeholder="••••••••••"
                    value={st.discogsToken||''} onChange={e=>setSt(p=>({...p,discogsToken:e.target.value}))}/>
                </div>
                <div style={{padding:'10px 16px',display:'flex',justifyContent:'flex-end'}}>
                  <div style={{display:'flex',justifyContent:'flex-end',marginTop:'var(--sp2)'}}><button className="sp-btn" onClick={()=>{if(notify)notify('Discogs settings saved');}} style={{height:32,padding:'0 16px',fontSize:'var(--fs-xs)'}}>save</button></div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ══ ADVANCED ══ */}
        {view==='advanced'&&(
          <>
            {/* Dashboard pulse */}
            <div className="sp-section">
              <div className="sp-section-lbl">dashboard pulse card</div>
              <div className="sp-card">
                {[['track today','showTrack'],['payday','showPayday'],['workouts','showWorkouts']].map(([label,key])=>(
                  <div key={key} className="sp-row">
                    <div className="sp-row-lbl">{label}</div>
                    <button className={`sp-toggle ${(st.pulseConfig?.[key]!==false)?'sp-toggle-on':'sp-toggle-off'}`}
                      onClick={()=>setSt(p=>({...p,pulseConfig:{...(p.pulseConfig||{}),showTrack:true,showPayday:true,showWorkouts:true,[key]:!(p.pulseConfig?.[key]!==false)}}))}>
                      <div className="sp-toggle-thumb" style={{transform:(st.pulseConfig?.[key]!==false)?'translateX(16px)':'translateX(0)'}}/>
                    </button>
                  </div>
                ))}
                <div className="sp-field-block">
                  <div className="sp-field-lbl">next payday</div>
                  <input type="date" className="sp-input" value={st.nextPayday||''} onChange={e=>setSt(p=>({...p,nextPayday:e.target.value}))}/>
                </div>
              </div>
            </div>

            {/* AI */}
            <div className="sp-section">
              <div className="sp-section-lbl">ai</div>
              <div className="sp-card">
                <div className="sp-field-block">
                  <div className="sp-field-lbl">anthropic api key</div>
                  <input type="password" className="sp-input sp-input-full" placeholder="sk-ant-…"
                    value={st.anthropicKey||''} onChange={e=>setSt(p=>({...p,anthropicKey:e.target.value}))}/>
                  {st.anthropicKey&&<div style={{fontFamily:'var(--sans)',fontSize:11,color:'var(--t3)'}}>key configured — using direct api</div>}
                </div>
                <div className="sp-row"><div className="sp-row-lbl">model</div><span className="sp-row-val">claude sonnet 4</span></div>
              </div>
            </div>

            {/* Notifications */}
            {subscribeToPush&&(
              <div className="sp-section">
                <div className="sp-section-lbl">notifications</div>
                <div className="sp-card">
                  <div className="sp-row">
                    <div><div className="sp-row-lbl">push notifications</div><div className="sp-row-sub">get reminders and alerts</div></div>
                    <button className="sp-btn" onClick={subscribeToPush}>enable</button>
                  </div>
                </div>
              </div>
            )}

            {/* Data */}
            <div className="sp-section">
              <div className="sp-section-lbl">data</div>
              <div className="sp-card">
                <div className="sp-row">
                  <div><div className="sp-row-lbl">export all data</div><div className="sp-row-sub">download full app state as json</div></div>
                  <button className="sp-btn sp-btn-outline" onClick={exportData}>export</button>
                </div>
                <div className="sp-row">
                  <div><div className="sp-row-lbl">clear all data</div><div className="sp-row-sub" style={{color:clearConfirm?'var(--accent-d)':undefined}}>{clearConfirm?'tap again to confirm — irreversible':'clears all tasks, events, workouts, vinyl, finance'}</div></div>
                  <button className="sp-btn sp-btn-danger" style={{color:clearConfirm?'var(--accent-d)':undefined}} onClick={clearData}>
                    {clearConfirm?'confirm clear':'clear all'}
                  </button>
                </div>
              </div>
            </div>

            {/* App info */}
            <div className="sp-footer">
              <div>mylo · v1.0</div>
              <div style={{marginTop:4}}>react · supabase · anthropic</div>
              <div style={{marginTop:8}}>made for kyle</div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
